<script setup>
import { ref, onMounted } from 'vue';
import api from '@/services/api';
import Swal from 'sweetalert2';
import logo from "@/assets/img/fondotam-blanco.png";
import SkeletonLoader from '@/components/SkeletonLoader.vue';

const loading = ref(true);
const regionalesData = ref([]);
const regionalSeleccionada = ref(null);
const activosRegional = ref([]);
const cargandoDetalle = ref(false);

const cargarDatos = async () => {
  try {
    const res = await api.get('dashboard/stats/');
    regionalesData.value = res.data.regionales || [];
  } catch (error) {
    Swal.fire('Error', 'No se pudieron cargar los datos de monitoreo.', 'error');
  } finally {
    loading.value = false;
  }
};

const verDetalleRegional = async (regional) => {
  regionalSeleccionada.value = regional;
  cargandoDetalle.value = true;
  try {
    const res = await api.get(`gestion-activos/?regional=${regional.id}`);

    const data = res.data.results || res.data;
    activosRegional.value = Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(error);
    Swal.fire('Error', 'No se pudieron cargar los activos de esta regional.', 'error');
  } finally {
    cargandoDetalle.value = false;
  }
};

const cerrarDetalle = () => {
  regionalSeleccionada.value = null;
  activosRegional.value = [];
};

// Utilidades visuales
const colorSalud = (salud) => {
  if (salud >= 70) return '#2dce89';
  if (salud >= 40) return '#fb6340';
  return '#f5365c';
};

const textoEstado = (salud) => {
  if (salud >= 70) return 'Estable';
  if (salud >= 40) return 'Alerta';
  return 'Crítico';
};

const classBadgeEstado = (salud) => {
  if (salud >= 70) return 'bg-gradient-success';
  if (salud >= 40) return 'bg-gradient-warning';
  return 'bg-gradient-danger';
};

const badgeSituacion = (sit) => {
  const map = {
    'Disponible': 'bg-gradient-info',
    'Asignado': 'bg-gradient-success',
    'Mantenimiento': 'bg-gradient-warning',
    'Baja': 'bg-gradient-danger'
  };
  return map[sit] || 'bg-secondary';
};

onMounted(() => {
  cargarDatos();
});
</script>

<template>
  <div class="container-fluid py-4">
    <!-- Loading -->
    <div v-if="loading" class="py-4">
      <div class="row mb-4">
        <div class="col-12">
          <SkeletonLoader type="text" :count="2" />
        </div>
      </div>
      <div class="row">
        <div v-for="i in 3" :key="'skel-reg-' + i" class="col-xl-4 col-md-6 mb-4">
          <SkeletonLoader type="card" :count="1" />
        </div>
      </div>
    </div>

    <div v-else>
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-lg border-radius-xl overflow-hidden card-custom">
            <div class="header-tamep px-4 py-4 d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <div class="logo-container-left me-4 p-1 rounded-lg shadow-sm"
                  style="border-radius: 8px; background-color: #ffffff !important;">
                  <img :src="logo" class="logo-img p-1" style="max-height: 45px;">
                </div>
                <div class="border-start ps-3 border-white opacity-8">
                  <h5 class="text-white font-weight-bolder text-uppercase mb-0">Monitoreo Global Operativo</h5>
                  <small class="text-white opacity-8">Supervisión en tiempo real de sedes ({{ regionalesData.length }}
                    regionales activas)</small>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <div v-if="!regionalSeleccionada" class="animate__animated animate__fadeIn">
        <div class="row">
          <div class="col-xl-4 col-md-6 mb-4" v-for="regional in regionalesData" :key="regional.nombre">
            <div class="card h-100 shadow-lg cursor-pointer tarjeta-hover card-custom"
              @click="verDetalleRegional(regional)">
              <div class="card-body p-4">
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h6 class="font-weight-bolder mb-0 text-azul-tamep">{{ regional.nombre }}</h6>
                    <p class="text-xs text-secondary mb-0">{{ regional.cantidad }} activos registrados</p>
                  </div>
                  <span :class="['badge', classBadgeEstado(regional.salud)]">
                    {{ textoEstado(regional.salud) }}
                  </span>
                </div>

                <label class="text-xxs font-weight-bold text-uppercase text-secondary mb-1 d-block">Salud
                  Promedio</label>
                <div class="d-flex align-items-center mb-3">
                  <div class="progress w-100 me-2" style="height: 10px;">
                    <div class="progress-bar" role="progressbar"
                      :style="{ width: regional.salud + '%', backgroundColor: colorSalud(regional.salud) }">
                    </div>
                  </div>
                  <span class="text-sm font-weight-bolder" :style="{ color: colorSalud(regional.salud) }">
                    {{ regional.salud }}%
                  </span>
                </div>

                <div class="row text-center border-top pt-3 mt-2">
                  <div class="col-6 border-end">
                    <h5 class="mb-0 font-weight-bolder">{{ regional.cantidad }}</h5>
                    <p class="text-xxs text-secondary text-uppercase mb-0">Total Activos</p>
                  </div>
                  <div class="col-6">
                    <h5 class="mb-0 font-weight-bolder text-danger">{{ regional.criticos || 0 }}</h5>
                    <p class="text-xxs text-secondary text-uppercase mb-0">Críticos</p>
                  </div>
                </div>
              </div>
              <div class="card-footer p-2 text-center bg-gray-50 border-0 border-radius-bottom">
                <span class="text-xs text-azul-tamep font-weight-bold">
                  Ver detalles <i class="fas fa-arrow-right ms-1"></i>
                </span>
              </div>
            </div>
          </div>

          <div v-if="regionalesData.length === 0" class="col-12 text-center py-5">
            <p class="text-muted">No hay datos de regionales disponibles.</p>
          </div>
        </div>

      </div>


      <div v-else class="animate__animated animate__fadeIn">

        <div class="mb-3">
          <button class="btn btn-sm btn-outline-white mb-0" @click="cerrarDetalle">
            <i class="fas fa-arrow-left me-1"></i> Volver a todas las regionales
          </button>
        </div>

        <div class="row">
          <div class="col-lg-4 mb-4">
            <div class="card h-100 shadow-xl border-0 tarjeta-fija card-custom overflow-hidden">
              <div class="header-tamep text-white text-center py-4 border-radius-top">
                <h5 class="text-white mb-0 font-weight-bolder text-uppercase">{{ regionalSeleccionada.nombre }}</h5>
                <p class="text-sm opacity-8 mb-0">Resumen Operativo</p>
              </div>
              <div class="card-body p-4 bg-section-custom rounded-bottom">
                <div class="text-center mb-4">
                  <span :class="['badge badge-lg', classBadgeEstado(regionalSeleccionada.salud)]">
                    Estado: {{ textoEstado(regionalSeleccionada.salud) }}
                  </span>
                </div>

                <label class="text-xs font-weight-bold text-uppercase text-secondary mb-1 d-block text-center">Salud
                  Promedio del Parque</label>
                <div class="progress mb-2" style="height: 15px;">
                  <div class="progress-bar" role="progressbar"
                    :style="{ width: regionalSeleccionada.salud + '%', backgroundColor: colorSalud(regionalSeleccionada.salud) }">
                  </div>
                </div>
                <h3 class="text-center font-weight-bolder mb-4"
                  :style="{ color: colorSalud(regionalSeleccionada.salud) }">
                  {{ regionalSeleccionada.salud }}%
                </h3>

                <ul class="list-group">
                  <li
                    class="list-group-item d-flex justify-content-between align-items-center border-0 px-0 mb-2 border-bottom">
                    <span class="text-sm font-weight-bold text-dark"><i class="fas fa-desktop text-secondary me-2"></i>
                      Total Activos</span>
                    <span class="badge bg-primary rounded-pill px-3 py-2">{{ regionalSeleccionada.cantidad }}</span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between align-items-center border-0 px-0">
                    <span class="text-sm font-weight-bold text-dark"><i
                        class="fas fa-exclamation-triangle text-danger me-2"></i> Equipos Críticos</span>
                    <span class="badge bg-danger rounded-pill px-3 py-2">{{ regionalSeleccionada.criticos || 0 }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="col-lg-8">
            <div class="card shadow-lg h-100 card-custom overflow-hidden">
              <div class="card-header card-header-custom pb-3 border-bottom">
                <div class="d-flex justify-content-between align-items-center">
                  <h6 class="mb-0 text-title-custom text-uppercase font-weight-bolder">Inventario de Activos</h6>
                  <span class="text-xs text-secondary font-weight-bold"><i
                      class="fas fa-circle-notch fa-spin text-primary me-1" v-if="cargandoDetalle"></i> {{
                        cargandoDetalle ? 'Cargando...' : activosRegional.length + ' encontrados' }}</span>
                </div>
              </div>

              <div class="card-body bg-section-custom px-0 pt-0 pb-2">
                <div v-if="cargandoDetalle" class="px-4 py-4">
                  <SkeletonLoader type="table-row" :count="4" />
                </div>

                <div v-else class="table-responsive p-0" style="max-height: 500px; overflow-y: auto;">
                  <table class="table align-items-center mb-0">
                    <thead class="bg-gray-50 sticky-top">
                      <tr>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Activo</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Ubicación
                        </th>
                        <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Situación</th>
                        <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Prob. Falla</th>
                        <th class="text-secondary opacity-7"></th>
                      </tr>
                    </thead>
                    <TransitionGroup tag="tbody" name="fade-list" class="bg-white">
                      <tr v-if="activosRegional.length === 0" key="empty-mon">
                        <td colspan="5" class="text-center py-4 text-muted">No hay activos registrados en esta regional.
                        </td>
                      </tr>
                      <tr v-for="a in activosRegional" :key="a.id">
                        <td>
                          <div class="d-flex px-3 py-1">
                            <div class="d-flex flex-column justify-content-center">
                              <h6 class="mb-0 text-sm">{{ a.nombre_especifico }}</h6>
                              <p class="text-xs text-secondary mb-0">{{ a.codigo_tamep }}</p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <p class="text-xs font-weight-bold mb-0">{{ a.ubicacion_nombre || '-' }}</p>
                          <p class="text-xxs text-secondary mb-0">{{ a.custodio_actual || 'Sin asignar' }}</p>
                        </td>
                        <td class="text-center">
                          <span :class="['badge badge-sm', badgeSituacion(a.situacion_activo)]">{{ a.situacion_activo
                            }}</span>
                        </td>
                        <td class="text-center">
                          <span v-if="a.probabilidad_falla != null"
                            :class="['text-xs font-weight-bold', a.probabilidad_falla >= 70 ? 'text-danger' : a.probabilidad_falla >= 40 ? 'text-warning' : 'text-success']">
                            {{ a.probabilidad_falla.toFixed(1) }}%
                          </span>
                          <span v-else class="text-xs text-muted">N/A</span>
                        </td>
                        <td class="align-middle text-center">
                          <router-link :to="`/activo/detalle/${a.id}`" class="btn btn-link text-dark px-3 mb-0 text-xs">
                            <i class="fas fa-eye text-primary me-1"></i> Detalle
                          </router-link>
                        </td>
                      </tr>
                    </TransitionGroup>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.app-transition {
  transition: all 0.3s ease;
}

.header-tamep {
  background: linear-gradient(135deg, #003366 0%, #001d3d 100%);
}

.border-radius-top {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.card-custom {
  background-color: #ffffff;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

.card-header-custom {
  background-color: transparent;
}

.bg-section-custom {
  background-color: #ffffff;
}

.text-title-custom {
  color: #344767;
}

.text-azul-tamep {
  color: #003366 !important;
}

/* Efecto hover elegante para las tarjetas del Grid */
.tarjeta-hover {
  cursor: pointer;
  transition: all 0.3s ease;
  border-top: 4px solid transparent;
}

.tarjeta-hover:hover {
  transform: translateY(-5px);
  border-top: 4px solid #003366;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1) !important;
}

.tarjeta-fija {
  border-top: 4px solid #003366 !important;
  overflow: hidden;
}

.shadow-xl {
  box-shadow: 0 20px 27px 0 rgba(0, 0, 0, 0.05) !important;
}

.bg-gray-50 {
  background-color: #f8f9fa !important;
}

.border-radius-bottom {
  border-bottom-left-radius: 1rem;
  border-bottom-right-radius: 1rem;
}
</style>

<style>
/* =========================================
   SOPORTE PARA MODO OSCURO (DARK MODE)
   ========================================= */
.dark-version .card-custom,
.dark-mode .card-custom {
  background-color: #1e1e1e !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5) !important;
  border: 1px solid #2d2d2d !important;
}

.dark-version .bg-section-custom,
.dark-mode .bg-section-custom {
  background-color: #1a1a1a !important;
}

.dark-version .card-header-custom,
.dark-mode .card-header-custom {
  border-bottom: 1px solid #333 !important;
}

.dark-version .bg-gray-50,
.dark-mode .bg-gray-50 {
  background-color: #2a2a2a !important;
  border-bottom: 1px solid #444 !important;
}

.dark-version .list-group-item,
.dark-mode .list-group-item {
  background-color: transparent !important;
  border-color: #333 !important;
  color: #e0e0e0 !important;
}

.dark-version .tarjeta-hover:hover,
.dark-mode .tarjeta-hover:hover {
  border-top-color: #4dabf7 !important;
}

.dark-version .tarjeta-fija,
.dark-mode .tarjeta-fija {
  border-top-color: #4dabf7 !important;
}

.dark-version .text-azul-tamep,
.dark-mode .text-azul-tamep {
  color: #4dabf7 !important;
}

.dark-version .text-title-custom,
.dark-mode .text-title-custom {
  color: #ffffff !important;
}

.dark-version .text-dark,
.dark-mode .text-dark {
  color: #ffffff !important;
}

.dark-version .table,
.dark-mode .table {
  color: #e0e0e0 !important;
}

.dark-version .table thead th,
.dark-mode .table thead th {
  background-color: #2a2a2a !important;
  color: #ffffff !important;
  border-bottom: 1px solid #444 !important;
}

.dark-version .table td,
.dark-mode .table td {
  border-bottom: 1px solid #333 !important;
}

.dark-version h5,
.dark-mode h5,
.dark-version h6,
.dark-mode h6 {
  color: #ffffff !important;
}
</style>